import { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowUpRight, X, Share2, Filter } from "lucide-react";
import JoinMovementDialog from "@/components/JoinMovementDialog";
import { api, SHARE_BASE } from "@/lib/api";
import { toast } from "sonner";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export default function WorldImpactMap() {
    const [projects, setProjects] = useState([]);
    const [team, setTeam] = useState([]);
    const [active, setActive] = useState(null);
    const [activeTeam, setActiveTeam] = useState(null);
    const [joinFor, setJoinFor] = useState(null);
    const [onlyJoinable, setOnlyJoinable] = useState(false);

    useEffect(() => {
        api.get("/projects").then(({ data }) => setProjects(data.projects || [])).catch(() => setProjects([]));
        api.get("/team").then(({ data }) => setTeam((data.team || []).filter((m) => m.mapped && m.lat != null && m.lng != null))).catch(() => setTeam([]));
    }, []);

    const visible = onlyJoinable ? projects.filter((p) => p.can_join) : projects;
    const activeProject = visible.find((p) => p.slug === active) || null;
    const activeMember = team.find((m) => m.slug === activeTeam) || null;

    const share = (project) => {
        const shareUrl = `${SHARE_BASE}/${project.slug}`;
        const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        window.open(li, "_blank", "noopener,noreferrer,width=680,height=680");
        if (navigator.clipboard) {
            navigator.clipboard.writeText(shareUrl).then(() => toast.success("Share link copied")).catch(() => {});
        }
    };

    return (
        <div className="relative w-full" data-testid="world-impact-map">
            {/* Filter toggle */}
            <div className="absolute top-2 right-2 md:top-4 md:right-4 z-20">
                <button
                    onClick={() => { setOnlyJoinable((v) => !v); setActive(null); }}
                    className={`label-mono flex items-center gap-2 px-3 py-2 border transition-colors backdrop-blur-md ${
                        onlyJoinable ? "bg-[var(--gold)] text-[var(--navy)] border-[var(--gold)]" : "bg-[rgba(6,17,36,0.6)] text-[var(--ivory)]/80 border-[rgba(255,255,240,0.2)] hover:border-[var(--gold)]"
                    }`}
                    data-testid="map-filter-joinable"
                >
                    <Filter className="w-3.5 h-3.5" />
                    {onlyJoinable ? "Showing joinable" : "Where you can join"}
                </button>
            </div>

            <div className="relative w-full aspect-[16/9]">
                <ComposableMap
                    projection="geoEqualEarth"
                    projectionConfig={{ scale: 195 }}
                    style={{ width: "100%", height: "100%", background: "transparent" }}
                >
                    <ZoomableGroup center={[15, 15]} zoom={1} minZoom={1} maxZoom={1.6}>
                        <Geographies geography={GEO_URL}>
                            {({ geographies }) =>
                                geographies.map((geo) => (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill="rgba(255,255,240,0.09)"
                                        stroke="rgba(201,162,75,0.35)"
                                        strokeWidth={0.5}
                                        style={{
                                            default: { outline: "none" },
                                            hover: { fill: "rgba(201,162,75,0.14)", outline: "none" },
                                            pressed: { outline: "none" },
                                        }}
                                    />
                                ))
                            }
                        </Geographies>

                        {visible.map((p) => {
                            const isActive = active === p.slug;
                            const color = p.can_join ? (isActive ? "#E4C57A" : "#C9A24B") : "#4FB07C";
                            return (
                                <Marker key={p.slug} coordinates={[p.lng, p.lat]} onClick={() => setActive(p.slug)}>
                                    <circle r={isActive ? 14 : 10} fill="none" stroke={color} strokeOpacity={0.35} strokeWidth={0.8} style={{ pointerEvents: "none" }}>
                                        <animate attributeName="r" values={isActive ? "10;20;10" : "6;12;6"} dur="2.4s" repeatCount="indefinite" />
                                        <animate attributeName="stroke-opacity" values="0.45;0.05;0.45" dur="2.4s" repeatCount="indefinite" />
                                    </circle>
                                    <circle
                                        r={isActive ? 4.5 : 3.5}
                                        fill={color}
                                        stroke="#0B1E3F"
                                        strokeWidth={1}
                                        style={{ cursor: "pointer", transition: "r 200ms ease" }}
                                        data-testid={`map-marker-${p.slug}`}
                                    />
                                </Marker>
                            );
                        })}

                        {/* Team ambassador markers (raised to avoid overlap with project dots) */}
                        {team.map((m) => {
                            const isActive = activeTeam === m.slug;
                            return (
                                <Marker key={`team-${m.slug}`} coordinates={[m.lng, m.lat]} onClick={() => { setActiveTeam(m.slug); setActive(null); }}>
                                    <g transform="translate(0,-13)" style={{ cursor: "pointer" }} data-testid={`team-marker-${m.slug}`}>
                                        <path d="M0,-9 L7,0 L0,9 L-7,0 Z" fill={isActive ? "#E4C57A" : "#FFFFF0"} stroke="#C9A24B" strokeWidth={1.4} />
                                        <circle cx="0" cy="0" r="2" fill="#0B1E3F" />
                                        <line x1="0" y1="9" x2="0" y2="13" stroke="#C9A24B" strokeWidth={1} />
                                    </g>
                                </Marker>
                            );
                        })}
                    </ZoomableGroup>
                </ComposableMap>

                {/* Legend */}
                <div className="absolute bottom-3 left-3 md:bottom-5 md:left-5 label-mono flex items-center gap-4 bg-[rgba(6,17,36,0.6)] backdrop-blur-md px-3 py-2 border border-[rgba(255,255,240,0.15)]" data-testid="map-legend">
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[var(--gold)]" /> <span className="text-[var(--ivory)]/70">Join now</span></span>
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: "#4FB07C" }} /> <span className="text-[var(--ivory)]/70">Coming soon</span></span>
                    <span className="flex items-center gap-2"><span className="w-2 h-2 rotate-45 bg-[var(--ivory)] border border-[var(--gold)]" /> <span className="text-[var(--ivory)]/70">Ambassador</span></span>
                </div>

                {/* Marker quick nav */}
                <div className="absolute top-14 right-3 md:top-16 md:right-5 hidden md:block max-w-[200px]">
                    <ul className="space-y-1 text-right">
                        {visible.map((p) => (
                            <li key={p.slug}>
                                <button
                                    onClick={() => setActive(p.slug)}
                                    className={`label-mono transition-colors ${active === p.slug ? "text-[var(--gold)]" : "text-[var(--ivory)]/55 hover:text-[var(--ivory)]"}`}
                                    data-testid={`map-list-${p.slug}`}
                                >
                                    {p.region}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Project card */}
            <AnimatePresence>
                {activeProject && (
                    <motion.aside
                        key={activeProject.slug}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 12 }}
                        transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
                        className="absolute left-4 md:left-8 bottom-16 md:bottom-20 max-w-[92%] md:max-w-md bg-[var(--navy-deep)]/95 backdrop-blur-lg border border-[var(--gold)] p-6 md:p-8 z-10"
                        data-testid={`project-card-${activeProject.slug}`}
                    >
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div className="label-mono text-[var(--gold)] flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5" /> {activeProject.region}
                            </div>
                            <button onClick={() => setActive(null)} aria-label="Close" className="text-[var(--ivory)]/50 hover:text-[var(--ivory)] transition-colors" data-testid="project-card-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <h3 className="serif-display text-2xl md:text-3xl leading-tight text-[var(--ivory)]">{activeProject.name}</h3>
                        <p className="mt-3 text-[var(--ivory)]/80 text-sm md:text-base leading-relaxed">{activeProject.summary}</p>
                        {activeProject.impact?.length > 0 && (
                            <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1">
                                {activeProject.impact.map((it) => (
                                    <li key={it} className="label-mono text-[var(--ivory)]/60 flex items-center gap-2">
                                        <span className="w-1 h-1 bg-[var(--emerald)]" /> {it}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="mt-6 flex items-center gap-3">
                            {activeProject.can_join ? (
                                <button className="btn-gold flex-1 justify-center" onClick={() => setJoinFor(activeProject)} data-testid={`join-movement-${activeProject.slug}`}>
                                    Join the movement <ArrowUpRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <span className="btn-ghost flex-1 justify-center opacity-70 cursor-default" data-testid={`coming-soon-${activeProject.slug}`}>Coming soon</span>
                            )}
                            <button
                                className="btn-ghost px-4"
                                onClick={() => share(activeProject)}
                                aria-label="Share on LinkedIn"
                                data-testid={`share-${activeProject.slug}`}
                            >
                                <Share2 className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.aside>
                )}
            </AnimatePresence>

            <JoinMovementDialog project={joinFor} onClose={() => setJoinFor(null)} />

            {/* Team member card overlay */}
            <AnimatePresence>
                {activeMember && (
                    <motion.aside
                        key={activeMember.slug}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 12 }}
                        transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
                        className="absolute right-4 md:right-8 bottom-16 md:bottom-20 max-w-[92%] md:max-w-sm bg-[var(--navy-deep)]/95 backdrop-blur-lg border border-[var(--gold)] p-5 md:p-6 z-10"
                        data-testid={`team-card-map-${activeMember.slug}`}
                    >
                        <div className="flex items-start justify-between gap-4 mb-3">
                            <div className="label-mono text-[var(--gold)] flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5" /> {activeMember.country}
                            </div>
                            <button onClick={() => setActiveTeam(null)} aria-label="Close" className="text-[var(--ivory)]/50 hover:text-[var(--ivory)] transition-colors" data-testid="team-card-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 shrink-0 overflow-hidden border border-[rgba(201,162,75,0.5)]">
                                <img src={activeMember.image} alt={activeMember.name} className="w-full h-full object-cover object-top" />
                            </div>
                            <div>
                                <h3 className="serif-display text-xl md:text-2xl leading-tight text-[var(--ivory)]">{activeMember.name}</h3>
                                <div className="label-mono text-[var(--gold)] mt-1">{activeMember.role}</div>
                            </div>
                        </div>
                        {activeMember.blurb && <p className="mt-4 text-sm text-[var(--ivory)]/75 leading-relaxed">{activeMember.blurb}</p>}
                    </motion.aside>
                )}
            </AnimatePresence>
        </div>
    );
}
